import { CalendarX2, Moon, Sun, Sunrise } from 'lucide-react'
import { dayPartOf, fmtLongDate, fmtTime, zonedParts, type DayPart, type LocalDate } from '../time'

export interface TimeSlot {
  start: string
  end: string
}

/** The title of a struck-through time (API.md §4.7 "Live updates"). */
export const JUST_TAKEN = 'Just booked by someone else'
/** The time panel while a week loads (UserFlows P-08 "Loading a week"). */
export const FINDING_TIMES = 'Finding open times…'

export interface TimeSlotsProps {
  /** The picked day, or null before one is picked. */
  date: LocalDate | null
  slots: TimeSlot[]
  /** The clinic's time zone: times are shown in clinic time (D-21). */
  timeZone: string
  /** The selected slot's start. */
  selected: string | null
  onSelect: (slot: TimeSlot) => void
  /**
   * Starts that were just taken by someone else (a 20 s poll dropped them, or a hold got `slot_taken`): drawn struck
   * through, `disabled`, titled "Just booked by someone else", and never selectable (Architecture P-04).
   */
  taken?: readonly string[]
  /** The week is loading: a spinner and "Finding open times…" in place of the prompt (task 07). */
  loading?: boolean
}

const GROUPS: { name: DayPart; icon: typeof Sunrise }[] = [
  { name: 'Morning', icon: Sunrise },
  { name: 'Afternoon', icon: Sun },
  { name: 'Evening', icon: Moon },
]

/**
 * P-08 time list (UserFlows P-08), ported from the mockup's `components/TimeSlots.tsx`: "Select a date to view
 * available time slots" until a day is picked; then the day, "N time slots" and the times grouped Morning /
 * Afternoon / Evening, in clinic time. Times another patient just took stay in the list struck through (the mockup's
 * `takenStart`, generalised to every time that disappears). While the week loads: "Finding open times…".
 */
export function TimeSlots({ date, slots, timeZone, selected, onSelect, taken = [], loading = false }: TimeSlotsProps) {
  if (loading) {
    return (
      <div className="slots-empty slots-loading">
        <span className="spinner" aria-hidden />
        <div role="status">{FINDING_TIMES}</div>
      </div>
    )
  }
  if (!date) {
    return (
      <div className="slots-empty">
        <CalendarX2 aria-hidden />
        <div>Select a date to view available time slots</div>
      </div>
    )
  }
  const gone = new Set(taken)
  const open = slots.filter((s) => !gone.has(s.start))
  const struck = [...gone]
    .filter((start) => zonedParts(start, timeZone).date === date)
    .map((start) => slots.find((s) => s.start === start) ?? { start, end: start })
  const all = [...open, ...struck].sort((a, b) => Date.parse(a.start) - Date.parse(b.start))
  return (
    <div>
      <div className="slots-head">
        <h2>{fmtLongDate(date)}</h2>
        <span className="hint">
          · {open.length} time slot{open.length === 1 ? '' : 's'}
        </span>
      </div>
      {open.length === 0 && (
        <div className="slots-empty">
          <CalendarX2 aria-hidden />
          <div>No times left on this day. Try another date.</div>
        </div>
      )}
      {GROUPS.map((g) => {
        const list = all.filter((s) => dayPartOf(s.start, timeZone) === g.name)
        if (list.length === 0) return null
        return (
          <div className="slot-group" key={g.name} role="group" aria-label={g.name}>
            <div className="slot-group-title" aria-hidden>
              <g.icon /> {g.name}
            </div>
            <div className="slot-grid">
              {list.map((s) => {
                const isTaken = gone.has(s.start)
                return (
                  <button
                    key={s.start}
                    type="button"
                    className={isTaken ? 'slot taken' : 'slot'}
                    aria-pressed={!isTaken && selected === s.start}
                    disabled={isTaken}
                    title={isTaken ? JUST_TAKEN : undefined}
                    onClick={() => {
                      if (!isTaken) onSelect(s)
                    }}
                  >
                    {fmtTime(s.start, timeZone)}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}
